import React, { useState, useEffect } from "react";
import axios from "axios";

const carrierFormats: Record<string, RegExp> = {
  "State Farm": /^[A-Z]{2}-\d{7}$/,
  Progressive: /^P\d{9}$/,
  Allstate: /^A\d{8}$/,
  GEICO: /^G\d{10}$/,
  "Liberty Mutual": /^L\d{8}-\d{2}$/,
};

interface PolicyNumberScreenProps {
  carrier: string;
  onSubmit?: (data: any) => void;
  onPrevious?: () => void;
  onNext?: (data?: any) => void;
  insuredId: string;
  workflowData: any;
  currentStep: number;
}

export default function PolicyNumberScreen({ carrier, onSubmit, onPrevious, onNext, insuredId, workflowData, currentStep }: PolicyNumberScreenProps) {
  const [policyNumber, setPolicyNumber] = useState("");
  const [signedDoc, setSignedDoc] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ policyNumber?: string; signedDoc?: string }>({});
  const searchParams = new URLSearchParams(window.location.search);

  // Check if it's ancillary flow
  const isAncillaryFlow = localStorage.getItem("ancillaryFlow") === "true";

  useEffect(() => {
    const stepData = workflowData[`step${currentStep}`];
    if (stepData) {
      setPolicyNumber(stepData.policyNumber || "");
      setNotes(stepData.notes || "");
    }
  }, [workflowData, currentStep]);

  const uploadFileToAMS = async (file: File | null, fileName: string, insuredId: string, policyId: string, policynumber: string) => {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("fileName", fileName);
    formData.append("insuredId", insuredId);
    formData.append("policyNumber", policynumber);
    formData.append("policyId", policyId);
    formData.append("ncToken", localStorage.getItem("NCToken") || "");

    try {
      const response = await axios.post(`${process.env.REACT_APP_ACE_API_Url}/api/ACE/uploadfiletoams`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    }
  };

  const insertNote = async (insuredId: string, quoteId: string, note: string, taskId: string = "") => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ACE_API_Url}/api/ACE/insertnote?ncToken=${localStorage.getItem("NCToken")}`,
        {
          insuredId,
          PolicyId: quoteId,
          note,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Insert note failed:", error);
      throw error;
    }
  };

  const validateForm = (): boolean => {
    const errors: { policyNumber?: string; signedDoc?: string } = {};

    // Skip validation for policy number and signed doc if ancillary flow
    if (!isAncillaryFlow) {
      if (!policyNumber.trim()) {
        errors.policyNumber = "Policy number is required";
      }

      // if (!signedDoc) {
      //   errors.signedDoc = "Signed document is required";
      // }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if data has already been submitted (coming back from e-signature page)
    const stepData = workflowData[`step${currentStep}`];
    const hasExistingData = stepData && stepData.policyNumber && workflowData.signDocumentFileIds && workflowData.signDocumentFileIds.length > 0;

    if (hasExistingData) {
      // Data already submitted, just navigate to next page without validation or API calls
      const data = { policyNumber, signedDoc, notes, signDocumentFileIds: workflowData.signDocumentFileIds, skipSave: true };
      if (onNext) onNext(data);
      return;
    }

    // Validate only for first-time submission
    if (!validateForm()) {
      return;
    }

    setUploading(true);
    try {
      // Determine the correct quote/policy ID
      const quoteId = isAncillaryFlow
        ? (workflowData?.step7?.selectedQuotes ?? workflowData?.step7?.SourceQuoteId)
        : workflowData?.quoteId;

      // Skip file upload for ancillary flow
      if (!isAncillaryFlow) {
        await uploadFileToAMS(signedDoc, `Policy_Document_${policyNumber}.pdf`, insuredId, quoteId, policyNumber);
      }
      if (notes.trim()) {
        await insertNote(insuredId, quoteId, notes, "");
      }

      let newSignDocumentFileIds = workflowData.signDocumentFileIds || [];
      if (workflowData.signDocumentFileIds === undefined || workflowData.signDocumentFileIds.length === 0) {
        const response = await axios.post(
          `${process.env.REACT_APP_ACE_API_Url}/api/ACE/uploadwayQuote?quoteId=${quoteId}&transactionId=${searchParams.get("transactionId")}&ncToken=${localStorage.getItem("NCToken")}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
            },
          },
        );
        newSignDocumentFileIds = response.data.data || [];
      }

      const data = { policyNumber, signedDoc, notes, signDocumentFileIds: newSignDocumentFileIds };
      if (onSubmit) onSubmit(data);
      if (onNext) onNext(data);
    } catch (error) {
      console.error("Error during submission:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page-container">
      {/* Page Header - consistent with other pages */}
      <div className="page-header mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Finalize Agency Bill & E-Signature</h1>
            <p className="text-sm text-gray-500">
              After payment, please go back to the carrier website to finalize the Agency Bill down payment and complete the e-signature process.
            </p>
          </div>
        </div>
      </div>
      <div className="form-grid">
        <form onSubmit={handleSubmit} className="policy-form">
          <div className="form-section-header form-group full-width">
            <h3 className="text-lg font-semibold mb-2">Policy Details</h3>
          </div>
          <div className="flex flex-wrap gap-6">
            {!isAncillaryFlow && (
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1" htmlFor="policyNumber">
                  Policy Number <span className="text-danger">*</span>
                </label>
                <input
                  id="policyNumber"
                  type="text"
                  className={`form-input compact-input ${validationErrors.policyNumber ? "error" : ""}`}
                  value={policyNumber}
                  onChange={(e) => {
                    setPolicyNumber(e.target.value);
                    if (validationErrors.policyNumber) {
                      setValidationErrors((prev) => ({ ...prev, policyNumber: undefined }));
                    }
                  }}
                  placeholder="Enter policy number"
                />
                {validationErrors.policyNumber && <span className="error-message">{validationErrors.policyNumber}</span>}
              </div>
            )}
            {!isAncillaryFlow && (
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1" htmlFor="signedDoc">
                  Import Signed Document
                </label>
                <input
                  id="signedDoc"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className={validationErrors.signedDoc ? "error" : ""}
                  onChange={(e) => {
                    setSignedDoc(e.target.files?.[0] || null);
                    if (validationErrors.signedDoc) {
                      setValidationErrors((prev) => ({ ...prev, signedDoc: undefined }));
                    }
                  }}
                />
                {validationErrors.signedDoc && <span className="error-message">{validationErrors.signedDoc}</span>}
              </div>
            )}
          </div>
          <div className="form-group mt-4">
            <label className="font-medium mb-1" htmlFor="notes">
              Producer Notes:
            </label>
            <textarea
              id="notes"
              className="form-input"
              value={notes}
              rows={5}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter any notes..."
            />
          </div>
          <div className="form-actions flex justify-between mt-8">
            <button type="button" className="btn btn-secondary" onClick={onPrevious} disabled={uploading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
              </svg>{" "}
              Previous
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? "Saving..." : "Continue"}
              {!uploading && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                </svg>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
